import React, { Fragment } from 'react';
import Picker from 'rc-picker/lib/Picker';
import generateConfig from 'rc-picker/lib/generate/moment';
import enEN from 'rc-picker/lib/locale/en_US';
import { t } from '../../utils/helpers';
import MainButton from '@ui-kit/components/mainButton';
import { gender } from '@base/utils/gender';
import { states } from '@base/utils/states';
import { useGoBack } from '../../features/routing/hooks/useGoBack';
import { Controller, useForm } from 'react-hook-form';
import useSession from '@base/utils/hooks/useSession';
import ReactFlagsSelect from 'react-flags-select';
import FormError from '@ui-kit/components/formError';
import { IAuthService } from '@base/features/auth/model/interfaces';
import { container } from 'tsyringe';
import { IModalProvider } from '@base/features/modal/model/interfaces';
import { ResponseCode } from '@base/utils/constants';
import styles from '../styles/completeRegistrationPage.module.scss';
import AuthWrapImage from '@base/features/auth/components/authWrapImage';
import moment, { Moment } from 'moment';
import TermsAndConditions from '@base/features/termsAndConditions/termsAndConditions';
// import { Loader } from '@ui-kit/components/loader';

const CompleteRegistrationPage = () => {
  const authService: IAuthService = container.resolve('authService');
  const modalProvider: IModalProvider = container.resolve('modalProvider');

  const { goBack } = useGoBack();

  const session = useSession();
  const {
    register,
    control,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm({ mode: 'all' });

  const onSubmit = handleSubmit(async (data, e) => {
    e?.preventDefault();
    try {
      const res = await authService.completeRegistration(
        data.firstName,
        data.lastName,
        data.yob.year(),
        data.gender,
        data.address.country,
        data.address.state,
        data.zip
      );

      if (res.code === ResponseCode.SUCCESS) {
        goBack();
      } else {
        modalProvider.show({
          title: t('error'),
          body: res.descr || '',
          buttons: [{ title: t('ok'), onClick: () => modalProvider.hide() }],
        });
      }
    } catch (e) {
      console.error(e);
      modalProvider.show({
        title: t('error'),
        body: t('unknown_error_try_again'),
        buttons: [{ title: t('ok'), onClick: () => modalProvider.hide() }],
      });
    }
  });

  return (
    <main className={styles.page}>
      <AuthWrapImage />
      <div className={styles.wrapper}>
        <h1>{t('complete_registration')}</h1>
        <form>
          <h2>{session?.usr}</h2>
          <input
            {...register('firstName', { required: true })}
            placeholder={t('first_name')}
            id={'FIRST_NAME'}
            type="text"
          />
          <input
            {...register('lastName', { required: true })}
            placeholder={t('last_name')}
            id={'LAST_NAME'}
            type="text"
          />
          <label htmlFor="yob">{t('birthday')}</label>
          <Controller
            control={control}
            name="yob"
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <Picker
                id="yob"
                value={value}
                locale={enEN}
                generateConfig={generateConfig}
                placeholder={t('year_of_birth')}
                picker="year"
                disabledDate={(date: Moment) => date.isAfter(moment())}
                onSelect={onChange}
              />
            )}
          />
          <label htmlFor="gender">{t('gender')}</label>
          <div className={styles.gender}>
            {gender.list().map((g) => (
              <Fragment key={g.code}>
                <input id={g.code} type="radio" value={g.code} {...register('gender', { required: true })} />
                <label htmlFor={g.code}>{t(g.name)}</label>
              </Fragment>
            ))}
          </div>
          <label htmlFor="country">{t('country')}</label>
          <Controller
            name="address.country"
            rules={{ required: true }}
            control={control}
            render={({ field: { value, onChange } }) => (
              <ReactFlagsSelect
                className="country-select"
                selected={value || ''}
                onSelect={onChange}
                searchable
              />
            )}
          />
          {watch('address.country') === 'US' && <label htmlFor="address.state">{t('state')}</label>}
          {watch('address.country') === 'US' && (
            <select {...register('address.state', { required: true })}>
              {states.list().map((state) => (
                <option value={state.code} key={state.code}>
                  {state.name}
                </option>
              ))}
            </select>
          )}
          <label htmlFor="address.zip">{t('zip')}</label>
          <input id="zip" {...register('address.zip', { required: true })} />
          <FormError errors={errors} field="address" />
          <Controller
            control={control}
            rules={{ required: true }}
            name="terms"
            render={({ field: { value, onChange } }) => (
              <TermsAndConditions id="t&c" status={value} handleStatus={onChange} />
            )}
          />
          <footer>
            <MainButton onClick={goBack}>{t('cancel')}</MainButton>
            <MainButton loading={isSubmitting} onClick={onSubmit} disabled={!isValid}>
              {t('signup')}
            </MainButton>
          </footer>
        </form>
      </div>
    </main>
  );
};

export default CompleteRegistrationPage;
